import { Controller } from "@hotwired/stimulus";

const LOCKED_VIEWPORT_CONTENT = "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";

// Keeps mobile zoom disabled even after Bootstrap opens and closes fullscreen modals.
//
// How this works:
// The layout starts with a strict viewport meta tag, but mobile browsers can
// become willing to zoom after focus changes, fullscreen modal transitions, or
// page restoration. This controller is attached once on <body>. It repeatedly
// writes the strict viewport content back during Bootstrap modal lifecycle
// events and blocks browser gesture events that represent pinch zoom.
export default class extends Controller {
  // Sets up the viewport lock and global gesture blockers.
  // Called automatically by Stimulus when data-controller="viewport-lock" connects.
  connect() {
    // Preserve this controller instance when enforceViewportLock, preventGestureZoom or
    // preventMultiTouchZoom are used as event callbacks.
    // Create a version of enforceViewportLock where this is permanently locked to this Stimulus controller.
    this.enforceViewportLock = this.enforceViewportLock.bind(this);
    this.preventGestureZoom = this.preventGestureZoom.bind(this);
    this.preventMultiTouchZoom = this.preventMultiTouchZoom.bind(this);

    // Set the initial locked viewport content immediately in case the page is restored from bfcache or a modal is already open on page load.
    this.enforceViewportLock();

    document.addEventListener("show.bs.modal", this.enforceViewportLock);
    document.addEventListener("shown.bs.modal", this.enforceViewportLock);
    document.addEventListener("hide.bs.modal", this.enforceViewportLock);
    document.addEventListener("hidden.bs.modal", this.enforceViewportLock);
    document.addEventListener("turbo:load", this.enforceViewportLock);
    window.addEventListener("pageshow", this.enforceViewportLock);

    document.addEventListener("gesturestart", this.preventGestureZoom, { passive: false });
    document.addEventListener("gesturechange", this.preventGestureZoom, { passive: false });
    document.addEventListener("gestureend", this.preventGestureZoom, { passive: false });
    document.addEventListener("touchmove", this.preventMultiTouchZoom, { passive: false });
  }

  // Removes all listeners added by connect().
  // Called automatically by Stimulus if the viewport-lock controller disconnects.
  disconnect() {
    document.removeEventListener("show.bs.modal", this.enforceViewportLock);
    document.removeEventListener("shown.bs.modal", this.enforceViewportLock);
    document.removeEventListener("hide.bs.modal", this.enforceViewportLock);
    document.removeEventListener("hidden.bs.modal", this.enforceViewportLock);
    document.removeEventListener("turbo:load", this.enforceViewportLock);
    window.removeEventListener("pageshow", this.enforceViewportLock);

    document.removeEventListener("gesturestart", this.preventGestureZoom);
    document.removeEventListener("gesturechange", this.preventGestureZoom);
    document.removeEventListener("gestureend", this.preventGestureZoom);
    document.removeEventListener("touchmove", this.preventMultiTouchZoom);
  }

  // Finds or creates the viewport meta tag, then writes the locked no-zoom content.
  // Called by connect(), Bootstrap modal events, turbo:load, and pageshow.
  enforceViewportLock() {
    let viewport = document.querySelector("meta[name='viewport']");

    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      document.head.appendChild(viewport);
    }

    viewport.setAttribute("content", LOCKED_VIEWPORT_CONTENT);
  }

  // Blocks Safari's gesture events so pinch zoom cannot start or continue.
  // Called by gesturestart, gesturechange, and gestureend document listeners.
  preventGestureZoom(event) {
    event.preventDefault();
  }

  // Blocks multi-touch movement because two or more touches usually means pinch zoom.
  // Called by the touchmove document listener.
  preventMultiTouchZoom(event) {
    // One-finger touchmove is normal scrolling. If there are fewer than two
    // active touches, let the browser handle the event so page scrolling still
    // works. Two or more active touches can become pinch zoom, so prevent it.
    if (event.touches.length < 2) return;

    event.preventDefault();
  }
}
